import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

// Properties
export const getProperties = () => api.get('/properties');
export const createProperty = (data) => api.post('/properties', data);
export const updateProperty = (id, data) => api.put(`/properties/${id}`, data);
export const deleteProperty = (id) => api.delete(`/properties/${id}`);

// Units
export const getUnits = (propertyId) => api.get('/units', { params: { property_id: propertyId } });
export const createUnit = (data) => api.post('/units', data);
export const updateUnit = (id, data) => api.put(`/units/${id}`, data);
export const deleteUnit = (id) => api.delete(`/units/${id}`);

// Tenants
export const getTenants = () => api.get('/tenants');
export const createTenant = (data) => api.post('/tenants', data);
export const updateTenant = (id, data) => api.put(`/tenants/${id}`, data);
export const deleteTenant = (id) => api.delete(`/tenants/${id}`);

// Receipts
export const uploadReceipt = (files, onProgress) => {
  const formData = new FormData();
  const fileList = Array.isArray(files) ? files : [files];
  fileList.forEach(f => formData.append('files', f));
  return api.post('/receipts/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
};
export const getReceipts = () => api.get('/receipts');
export const getReceipt = (id) => api.get(`/receipts/${id}`);
export const getReceiptPage = (id, pageIndex) => api.get(`/receipts/${id}/pages/${pageIndex}`);
export const updateReceipt = (id, data) => api.put(`/receipts/${id}`, data);
export const deleteReceipt = (id) => api.delete(`/receipts/${id}`);

// Kostenstellen
export const getKostenstellen = () => api.get('/kostenstellen');
export const createKostenstelle = (data) => api.post('/kostenstellen', data);
export const updateKostenstelle = (id, data) => api.put(`/kostenstellen/${id}`, data);
export const deleteKostenstelle = (id) => api.delete(`/kostenstellen/${id}`);

// Reports
export const getJahresabrechnung = (year, propertyId) => 
  api.get('/reports/jahresabrechnung', { params: { year, property_id: propertyId } });

// Transactions
export const getTransactions = (params) => api.get('/transactions', { params });
export const createTransaction = (data) => api.post('/transactions', data);
export const updateTransaction = (id, data) => api.put(`/transactions/${id}`, data);
export const deleteTransaction = (id) => api.delete(`/transactions/${id}`);

// Dashboard
export const getDashboardStats = (params) => api.get('/dashboard/stats', { params });

// Auth
export const changePassword = (data) => api.post('/auth/change-password', data);

// Excel & PDF Export
export const getJahresabrechnungExcelUrl = (year, propertyId) => {
  const params = new URLSearchParams({ year: year.toString() });
  if (propertyId) params.append('property_id', propertyId);
  return `${API_URL}/api/reports/jahresabrechnung/excel?${params.toString()}`;
};

export const getJahresabrechnungPdfUrl = (year, propertyId) => {
  const params = new URLSearchParams({ year: year.toString() });
  if (propertyId) params.append('property_id', propertyId);
  return `${API_URL}/api/reports/jahresabrechnung/pdf?${params.toString()}`;
};

// Unit Abrechnung (Admin)
export const getUnitAbrechnung = (unitId, year) =>
  api.get(`/reports/einheit-abrechnung/${unitId}`, { params: { year } });
export const getAllUnitsAbrechnung = (year, propertyId) =>
  api.get('/reports/alle-einheiten-abrechnung', { params: { year, property_id: propertyId } });

// Tenant Portal
export const portalMe = () => api.get('/portal/me');
export const portalUpdateMe = (data) => api.put('/portal/me', data);
export const portalChangePassword = (data) => api.post('/portal/change-password', data);
export const portalGetAbrechnung = (year) => api.get(`/portal/abrechnung/${year}`);
export const portalGetMessages = () => api.get('/portal/messages');
export const portalSendMessage = (content) => api.post('/portal/messages', { content });

// Admin Messages
export const adminGetMessages = (tenantId) => api.get(`/messages/${tenantId}`);
export const adminSendMessage = (tenantId, content) => api.post('/messages', { tenant_id: tenantId, content });
export const adminUnreadCount = () => api.get('/messages/unread/count');

// AI (IONOS)
export const aiListModels = () => api.get('/ai/models');
export const aiCheckReceipt = (receiptId, config) => api.post(`/receipts/${receiptId}/ai-check`, config);
export const aiAcceptSuggestion = (auditId, data) => api.post(`/ai-audit-logs/${auditId}/accept`, data);
export const aiListAuditLogs = (params) => api.get('/ai-audit-logs', { params });
export const aiGetAuditLog = (id) => api.get(`/ai-audit-logs/${id}`);
export const aiAuditCsvUrl = () => `${API_URL}/api/ai-audit-logs/export/csv`;

// Billing
export const adminKiAbrechnung = (year, month) => api.get(`/admin/ki-abrechnung/${year}/${month}`);
export const adminKiAbrechnungMonths = () => api.get('/admin/ki-abrechnung/available-months');
export const adminKiAbrechnungCsvUrl = (year, month) => `${API_URL}/api/admin/ki-abrechnung/${year}/${month}/csv`;
export const billingMe = (year, month) => api.get('/billing/me', { params: { year, month } });
export const billingMyHistory = () => api.get('/billing/me/history');

// KI Budget Brake
export const adminGetKiBudget = () => api.get('/admin/ki-budget');
export const adminSetKiBudget = (eur) => api.put('/admin/ki-budget', { monthly_budget_eur: eur });

// Rent payments (Mieteinnahmen)
export const listRentPayments = (params) => api.get('/rent-payments', { params });
export const upsertRentPayment = (data) => api.put('/rent-payments', data);
export const deleteRentPayment = (tenant_id, year, month) =>
  api.delete('/rent-payments', { params: { tenant_id, year, month } });
export const regenerateRentPayments = (tenant_id) =>
  api.post(`/rent-payments/regenerate/${tenant_id}`);

// Profit & Loss
export const getProfitLoss = (year, month) =>
  api.get('/profit-loss', { params: month ? { year, month } : { year } });

// System Notice (Update / Wartung)
export const getSystemNotice = () => api.get('/system-notice');
export const adminGetSystemNotice = () => api.get('/admin/system-notice');
export const adminSetSystemNotice = (data) => api.put('/admin/system-notice', data);

// Security Audit
export const auditListEvents = (params) => api.get('/admin/audit/events', { params });
export const auditSystemStatus = () => api.get('/admin/audit/system-status');
export const auditStorage = () => api.get('/admin/audit/storage');
export const auditSecurityStats = () => api.get('/admin/audit/security-stats');

// Meters (Zähler)
export const listMeters = (params) => api.get('/meters', { params });
export const createMeter = (data) => api.post('/meters', data);
export const updateMeter = (id, data) => api.put(`/meters/${id}`, data);
export const deleteMeter = (id) => api.delete(`/meters/${id}`);
export const listMeterReadings = (meterId) => api.get(`/meters/${meterId}/readings`);
export const createMeterReading = (meterId, data) => api.post(`/meters/${meterId}/readings`, data);
export const updateMeterReading = (readingId, data) => api.put(`/meter-readings/${readingId}`, data);
export const verifyMeterReading = (readingId) => api.post(`/meter-readings/${readingId}/verify`);
export const deleteMeterReading = (readingId) => api.delete(`/meter-readings/${readingId}`);
export const getMeterReadingPhoto = (readingId) => api.get(`/meter-readings/${readingId}/photo`);
export const getMeterConsumption = (meterId, fromDate, toDate) =>
  api.get(`/meters/${meterId}/consumption`, { params: { from_date: fromDate, to_date: toDate } });

// Portal: Meters
export const portalListMeters = () => api.get('/portal/meters');
export const portalListMeterReadings = (meterId) => api.get(`/portal/meters/${meterId}/readings`);
export const portalSubmitMeterReading = (meterId, data) => api.post(`/portal/meters/${meterId}/readings`, data);

// Admin: Reset any user's or tenant's password
export const adminResetPassword = (data) => api.post('/auth/admin/reset-password', data);

// HV-Abrechnungen (Hausverwalter-Abrechnungs-Import)
export const uploadHvAbrechnung = (formData) => api.post('/hv-abrechnungen/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const extractHvAbrechnung = (id) => api.post(`/hv-abrechnungen/${id}/extract`);
export const listHvAbrechnungen = () => api.get('/hv-abrechnungen');
export const getHvAbrechnung = (id) => api.get(`/hv-abrechnungen/${id}`);
export const updateHvAbrechnung = (id, data) => api.put(`/hv-abrechnungen/${id}`, data);
export const deleteHvAbrechnung = (id) => api.delete(`/hv-abrechnungen/${id}`);
export const applyHvAbrechnung = (id, data) => api.post(`/hv-abrechnungen/${id}/apply`, data);
export const getHvAbrechnungPage = (id, pageIdx) => api.get(`/hv-abrechnungen/${id}/pages/${pageIdx}`);

// Compliance (Lizenz-Prüfung)
export const complianceScan = (mode = 'saas', refresh = false) => api.get('/admin/compliance/scan', { params: { mode, refresh } });
export const complianceRequestFix = (data) => api.post('/admin/compliance/request-fix', data);
export const complianceTemplate = (name) => api.get(`/admin/compliance/template/${name}`);
export const complianceCheckNow = () => api.post('/admin/compliance/check-now');
export const complianceHistory = (limit = 10) => api.get('/admin/compliance/history', { params: { limit } });

export default api;
